import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Bookmark, BookmarkPlus, Trash2, Clock, UserCheck, Flag, X, Loader2,
} from "lucide-react";

/**
 * Queue workspace toolbar (participant-console-009).
 *
 * Two concerns, both partner-scoped:
 *
 *  1. SAVED FILTERS / SEGMENTS — a switcher over the calling partner's named
 *     queue slices (GET/POST/DELETE /partner/console/saved-filters). Applying
 *     a saved filter replays its persisted criteria back into the queue's
 *     query state via `onApplyFilter`. Filters persist per partner principal
 *     server-side, so they re-appear on return.
 *
 *  2. BULK-SAFE TRIAGE BAR — when rows are multi-selected (checkboxes in
 *     QueueTable), this surfaces snooze / assign / flag for the whole
 *     selection (POST /partner/console/triage). These are TRIAGE ONLY.
 *
 * HARD NON-GOAL (PINNED #4/#7): there is NO bulk countersign and NO bulk
 * reject control here, by design. The countersign is a per-document legal act
 * (006) reached only from the single-participant review pane. This toolbar
 * never renders or calls a decision action; the backend `/triage` endpoint
 * structurally refuses any non-triage verb.
 */

const TRIAGE = [
  { action: "snooze", label: "Snooze", Icon: Clock },
  { action: "assign", label: "Claim", Icon: UserCheck },
  { action: "flag", label: "Flag", Icon: Flag },
];

const QueueToolbar = ({
  selectedIds = [],
  currentCriteria = {},
  onApplyFilter,
  onClearSelection,
  onTriageDone,
}) => {
  const [filters, setFilters] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [activeFilterId, setActiveFilterId] = useState("");

  const [savingOpen, setSavingOpen] = useState(false);
  const [newName, setNewName] = useState("");

  // Snooze needs a reason + until-date; the inline panel collects them.
  const [snoozePanel, setSnoozePanel] = useState(false);
  const [snoozeReason, setSnoozeReason] = useState("");
  const [snoozeUntil, setSnoozeUntil] = useState("");

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const selectionCount = selectedIds.length;

  const loadFilters = useCallback(async () => {
    setLoadingFilters(true);
    try {
      const r = await api.get("/partner/console/saved-filters");
      setFilters(r.data?.filters || []);
    } catch {
      setFilters([]);
    } finally {
      setLoadingFilters(false);
    }
  }, []);

  useEffect(() => { loadFilters(); }, [loadFilters]);

  const applyFilter = (f) => {
    setActiveFilterId(f.id);
    if (onApplyFilter) onApplyFilter(f.criteria || {});
  };

  const saveCurrent = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.post("/partner/console/saved-filters", {
        name,
        criteria: currentCriteria || {},
      });
      setNewName("");
      setSavingOpen(false);
      await loadFilters();
      setMsg({ kind: "ok", text: `Saved "${name}".` });
    } catch (err) {
      setMsg({ kind: "err", text: err.response?.data?.detail || "Could not save filter." });
    } finally {
      setBusy(false);
    }
  };

  const deleteFilter = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/partner/console/saved-filters/${id}`);
      if (activeFilterId === id) setActiveFilterId("");
      await loadFilters();
    } catch (err) {
      setMsg({ kind: "err", text: err.response?.data?.detail || "Could not delete filter." });
    } finally {
      setBusy(false);
    }
  };

  const runTriage = async (action, extra = {}) => {
    if (selectionCount === 0) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post("/partner/console/triage", {
        action,
        user_ids: selectedIds,
        ...extra,
      });
      setMsg({
        kind: "ok",
        text: `${action} applied to ${r.data?.applied_count ?? 0} item(s).`,
      });
      setSnoozePanel(false);
      setSnoozeReason("");
      setSnoozeUntil("");
      if (onClearSelection) onClearSelection();
      if (onTriageDone) onTriageDone();
    } catch (err) {
      setMsg({ kind: "err", text: err.response?.data?.detail || "Triage action failed." });
    } finally {
      setBusy(false);
    }
  };

  const onTriageClick = (action) => {
    if (action === "snooze") {
      setSnoozePanel((s) => !s);
      return;
    }
    runTriage(action);
  };

  const submitSnooze = (e) => {
    e.preventDefault();
    if (!snoozeReason.trim() || !snoozeUntil) return;
    runTriage("snooze", { reason: snoozeReason.trim(), snooze_until: snoozeUntil });
  };

  return (
    <div data-testid="queue-toolbar" className="mb-3">
      {/* SAVED FILTERS SWITCHER */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 uppercase tracking-wide">
          <Bookmark size={13} /> Saved views
        </span>

        {loadingFilters ? (
          <span className="text-xs text-slate-400">Loading…</span>
        ) : filters.length === 0 ? (
          <span className="text-xs text-slate-400">None yet</span>
        ) : (
          filters.map((f) => (
            <span
              key={f.id}
              data-testid={`saved-filter-${f.id}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
                activeFilterId === f.id
                  ? "border-blue-400 bg-blue-50 text-blue-900"
                  : "border-slate-300 bg-white text-slate-700"
              }`}
            >
              <button
                type="button"
                onClick={() => applyFilter(f)}
                data-testid={`saved-filter-apply-${f.id}`}
                className="hover:underline"
              >
                {f.name}
              </button>
              <button
                type="button"
                onClick={() => deleteFilter(f.id)}
                disabled={busy}
                data-testid={`saved-filter-delete-${f.id}`}
                className="text-slate-400 hover:text-red-600"
                aria-label={`Delete saved view ${f.name}`}
              >
                <Trash2 size={11} />
              </button>
            </span>
          ))
        )}

        {savingOpen ? (
          <form onSubmit={saveCurrent} className="inline-flex items-center gap-1">
            <Input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name this view…"
              data-testid="saved-filter-name"
              className="h-7 w-40 bg-white text-slate-900 border-slate-300 text-xs"
            />
            <Button
              type="submit"
              size="sm"
              disabled={busy || !newName.trim()}
              data-testid="saved-filter-save"
              className="h-7 px-2 text-xs"
            >
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { setSavingOpen(false); setNewName(""); }}
              className="h-7 px-1 text-xs"
            >
              <X size={13} />
            </Button>
          </form>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setSavingOpen(true)}
            data-testid="saved-filter-new"
            className="h-7 px-2 text-xs border-slate-300 text-slate-700"
          >
            <BookmarkPlus size={13} className="mr-1" /> Save current
          </Button>
        )}
      </div>

      {/* BULK-SAFE TRIAGE BAR — only while a multi-selection exists */}
      {selectionCount > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2"
        >
          <span className="text-xs font-medium text-blue-900" data-testid="bulk-selection-count">
            {selectionCount} selected
          </span>
          <span className="text-xs text-blue-700">Triage:</span>
          {TRIAGE.map(({ action, label, Icon }) => (
            <Button
              key={action}
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => onTriageClick(action)}
              data-testid={`bulk-${action}`}
              className="h-7 px-2 text-xs border-blue-300 bg-white text-blue-900"
            >
              {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Icon size={13} className="mr-1" />}
              {label}
            </Button>
          ))}
          <button
            type="button"
            onClick={onClearSelection}
            data-testid="bulk-clear"
            className="ml-auto text-xs text-blue-700 hover:underline"
          >
            Clear selection
          </button>
          {/* Intentionally NO bulk countersign / reject control — per
              participant-console-009 PINNED #4/#7. The legal act stays
              per-document in the single-participant review pane. */}
        </div>
      )}

      {/* SNOOZE detail panel (reason + until-date required) */}
      {selectionCount > 0 && snoozePanel && (
        <form
          onSubmit={submitSnooze}
          data-testid="bulk-snooze-panel"
          className="mt-2 flex flex-wrap items-end gap-2 rounded-md border border-slate-200 bg-white px-3 py-2"
        >
          <label className="flex flex-col text-xs text-slate-600">
            Defer until
            <input
              type="date"
              value={snoozeUntil}
              onChange={(e) => setSnoozeUntil(e.target.value)}
              data-testid="bulk-snooze-until"
              className="mt-0.5 h-8 rounded border border-slate-300 px-2 text-sm text-slate-900"
            />
          </label>
          <label className="flex flex-1 flex-col text-xs text-slate-600 min-w-[12rem]">
            Reason
            <Input
              value={snoozeReason}
              onChange={(e) => setSnoozeReason(e.target.value)}
              placeholder="Why defer these?"
              data-testid="bulk-snooze-reason"
              className="mt-0.5 h-8 bg-white text-slate-900 border-slate-300 text-sm"
            />
          </label>
          <Button
            type="submit"
            size="sm"
            disabled={busy || !snoozeReason.trim() || !snoozeUntil}
            data-testid="bulk-snooze-submit"
            className="h-8 px-3 text-xs"
          >
            Snooze {selectionCount}
          </Button>
        </form>
      )}

      {msg && (
        <p
          data-testid="toolbar-msg"
          className={`mt-2 text-xs ${msg.kind === "ok" ? "text-emerald-700" : "text-red-600"}`}
        >
          {msg.text}
        </p>
      )}
    </div>
  );
};

export default QueueToolbar;
